// src/game/game.ts — Maç döngüsü (ARCHITECTURE §3 kare akışı birebir; §12 WP9). Sorumluluklar:
//   • gerçek sim sistemlerinin enjeksiyonu (`realSystems` artık sim/systems.ts'te — three'siz, sunucu da onu kullanır) + MoveDef.clipIdx çözümü;
//   • statik varlıklar (clipdata.json + klip kütüphanesi) süreçte bir kez (`getClipData` / `getClipLibrary`), maç varlıkları ilerlemeyle;
//   • kare saati frameLoop.ts'de (accumulator + dt snap + tavan sıfırlama + uyarlanabilir interpolasyon + slow-mo; DOM'suz test edilir);
//     burada duraklat, girdi → bot → step → presenter sırası ve süre ölçümü (msSim yalnız world.step; msBot / msPresent ayrı — bulgu 1);
//   • oyuncu girdisi (InputManager: klavye+mouse+pad; aimYaw çözümleyici = kamera ışını → oyuncu zemin düzlemi), botlar (think/frame);
//   • presenter: olaylar → kamera sarsıntısı / vfx / ses / hud; window.__game debug kancası (olay günlüğü, dünya, botlar);
//   • eğitim modu (`tutorial`): bot yerine Manken kuklası + TutorialMode kancaları (adım sonrası koruma, olay → ders, ders → panel).
// Sim bu dosyayı bilmez (game → view → anim → sim → core/config tek yön). Sıcak yol tahsissiz değil (game katmanı), ama kare başına
// döngülerde dizi/nesne yaratılmaz; olay günlüğü dizgeleri yalnız olay olunca.
import * as THREE from 'three';
import type { Bot, ClipDataSet, Difficulty, SimEvent, SimSystems, SimWorld, Vec3, MatchState } from '../core/types.ts';
import { Btn, Ev, HitKind, inputOf } from '../core/types.ts';
import { evName, type EventPool } from '../core/events.ts';
import { yawOf } from '../core/vec3.ts';
import { MAX_FIGHTERS, NO_ID, TICK_DT } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import { ARENA } from '../config/arena.ts';
import { CHARACTERS, type CharacterDef } from '../config/characters.ts';
import { REQUIRED_SETS, SET_FILES, UPPER_ALLOWED } from '../config/clipTable.ts';
import {
  ALL_PROP_IDS, LoadProgress, createProp, fetchClipData, kitMaterial, loadCharacter, loadGLTF, loadKitGeometry, loadPropTemplate,
} from '../view/assets.ts';
import type { Stage } from '../view/stage.ts';
import { ArenaView } from '../view/arenaView.ts';
import { FighterView } from '../view/fighterView.ts';
import { ItemViews } from '../view/itemView.ts';
import { ArenaCamera } from '../view/camera.ts';
import { Vfx } from '../view/vfx.ts';
import type { Audio } from '../view/audio.ts';
import { Hud } from '../view/hud.ts';
import type { DebugDraw } from '../view/debugDraw.ts';
import { parseClipData, upperLayerAllowed, type ClipDataJson } from '../anim/clipData.ts';
import { loadClipLibrary, loadLazySets, type ClipLibrary } from '../anim/clipLibrary.ts';
import { createAnimController, type AnimControllerImpl } from '../anim/animController.ts';
import { decide } from '../anim/animMap.ts';
import { createWorld, type World } from '../sim/world.ts';
import { realSystems } from '../sim/systems.ts';
import { windupPressesOf } from '../sim/grab.ts';
import { resolveMoveClips } from '../sim/hitbox.ts';
import { createSlowMo, requestHitSlowMo, requestMatchEndSlowMo, timeScaleOf } from '../view/slowMo.ts';
import { InputRecorder, quantizeAndRecord, toJson, type RecordingJson } from '../net/recording.ts';
import type { NetClient } from '../net/client.ts';
import type { SlowMoState } from '../core/types.ts';
import { createBot } from '../sim/bot/brain.ts';
import type { DummyBot } from '../sim/bot/dummy.ts';
import { TutorialMode, createTutorialDummy, tutorialArena } from './tutorialMode.ts';
import { keyEdgeBrakeOn } from '../input/inputManager.ts';
import { KeyboardMouseSource } from '../input/keyboard.ts';
import { padSlotToIndex } from '../input/gamepad.ts';
import { LocalPlayers, type LocalPlayerSlot } from '../input/localPlayers.ts';
import { KEYBOARD_PLAYER, MAX_LOCAL_PLAYERS, type LocalPlayer } from '../config/players.ts';
import { RUMBLE, type RumblePreset } from '../input/rumble.ts';
import { attachOverlayNav } from '../input/uiNav.ts';
import { toggleFullscreen } from '../view/fullscreen.ts';
import { DT_CAP, clampDt, createFrameClock, createNetClock, netPlayoutRate, renderAlpha, restartRateMeasure, sampleRate, stepAccumulator, type FrameClock } from './frameLoop.ts';
// data/ public altında değil: Vite `?url` ile varlık olarak paketlenir (dev: /data/clipdata.json; build: hash'li asset).
import clipDataUrl from '../../data/clipdata.json?url';

/* ------------------------------------------------------------- sabitler (kare saati sabitleri frameLoop.ts'de) */
/** Olay günlüğü halkası (window.__game.events). */
const EVENT_LOG_CAP = 400;

/* ------------------------------------------------------------- statik varlıklar (süreçte bir kez) */

let clipDataP: Promise<ClipDataSet> | null = null;
/** data/clipdata.json → ClipDataSet (parseClipData three'siz; sim + anim ortak). Menüye dönüşte önbellek kalır. */
export function getClipData(): Promise<ClipDataSet> {
  if (!clipDataP) clipDataP = fetchClipData(clipDataUrl, (json) => parseClipData(json as ClipDataJson));
  return clipDataP;
}
let libP: Promise<ClipLibrary> | null = null;
/** Zorunlu 5 klip seti + türevler; `#upper` kapısı clipdata hipsYawRange ile (ANIMATION §4). Tembel setler maç başlayınca. */
export function getClipLibrary(): Promise<ClipLibrary> {
  if (!libP) {
    libP = (async () => {
      const clipData = await getClipData();
      const allowUpper = (base: string): boolean => {
        try { return upperLayerAllowed(clipData, base); } catch { return UPPER_ALLOWED.has(base); }   // clipdata'da olmayan kanonik ad → liste
      };
      return loadClipLibrary(loadGLTF, { allowUpper });
    })();
  }
  return libP;
}

export interface MatchAssets { clipData: ClipDataSet; lib: ClipLibrary; kitGeometries: Map<string, THREE.BufferGeometry> }
/** Menüde "DÖVÜŞ" (ARCHITECTURE §6): Promise.all — klip setleri, seçilen karakterler, proplar, kit modelleri, clipdata; ilerleme = tamamlanan/istek. */
export async function loadMatchAssets(chars: readonly CharacterDef[], progress: LoadProgress): Promise<MatchAssets> {
  const clipData = await progress.track(getClipData(), 'clipdata');
  const kitModels = [...new Set(ARENA.platforms.map((p) => p.kitModel))];
  const setLoads = REQUIRED_SETS.map((s) => progress.track(loadGLTF(SET_FILES[s]), 'klip ' + s));
  const charLoads = [...new Set(chars.map((c) => c.url))].map((u) => progress.track(loadGLTF(u), u.slice(u.lastIndexOf('/') + 1)));
  const propLoads = ALL_PROP_IDS.map((id) => progress.track(loadPropTemplate(id), id));
  const kitLoads = kitModels.map((id) => progress.track(loadKitGeometry(id).then((g) => [id, g] as const), id));
  const [, , , kits] = await Promise.all([Promise.all(setLoads), Promise.all(charLoads), Promise.all(propLoads), Promise.all(kitLoads)]);
  const lib = await progress.track(getClipLibrary(), 'klip türevleri');
  return { clipData, lib, kitGeometries: new Map(kits) };
}

/* ------------------------------------------------------------- oyun */

export interface GameFighterSpec { charIdx: number; isBot: boolean; difficulty: Difficulty }
export interface GameOptions {
  stage: Stage;
  assets: MatchAssets;
  fighters: readonly GameFighterSpec[];
  /**
   * Aynı ekrandaki insan oyuncular (0–4; config/players.ts sözleşmesi, menüden gelir — game/roster.ts kurar).
   * BOŞ → izleyici/duman testi: girdi yok, kamera sarsıntısı kimseyi "ilgilendirmez", HUD kimseyi işaretlemez (kadraj 0. dövüşçüde).
   * `fighterId` kadrodaki sırayı gösterir (roster.ts insanları başa koyar); < 0 verilirse insan dövüşçüler sırayla eşlenir.
   * `padSlot` HAM yuva değil "kaçıncı bağlı pad"tir (menü öyle üretir; burada `padSlotToIndex` ile ham yuvaya çevrilir).
   */
  players: readonly LocalPlayer[];
  seed: number;
  audio: Audio;
  debugDraw: DebugDraw | null;
  /** ?stats=1 kutusu. */
  stats?: boolean;
  /** Geri sayım (varsayılan true; DESIGN §13). */
  countdown?: boolean;
  /** Eğitim modu (docs/TUTORIAL.md): kadro = oyuncu + Manken kukla; ölüm yok, maç bitişi yok, eşya yalnız ders talebiyle doğar.
   *  Kukla `createDummy` ile kurulur (bot yerine), ders paneli ve kuralları TutorialMode'da. */
  tutorial?: boolean;
  hudContainer?: HTMLElement;
  /** `?record=1`: maçın TÜM girdilerini kaydet (NETWORK.md §7.3). Kayıt `window.__game.recording()` ile alınır ve
   *  `node tools/replay.mjs <dosya>` ile headless oynatılır. Sunucunun yayınlayacağı verinin ta kendisi. */
  record?: boolean;
  /** Kadro çekilişinin tohumu — kayda yazılır ki oynatan taraf aynı kadroyu yeniden üretebilsin (roster.ts rosterRand). */
  rosterSeed?: number;
  /**
   * AĞ MAÇI: verilirse dünya BU İSTEMCİDEN gelir (sunucunun seed'i ve kadrosuyla kurulmuş) ve tick'ler
   * accumulator yerine otoritenin onayladığı girdiden sürülür (NETWORK.md FAZ 1).
   * Botlar yerel koşmaz (otoritede koşuyorlar), girdi kaydı kapalıdır (kayıt otoritenin işi), zaman
   * genişletmesi uygulanmaz (sim saati sunucuya kilitli — değişmez 12).
   */
  net?: NetClient;
  /** Presenter'a ek dinleyici (duman testi sayaçları). */
  onEvent?: (e: SimEvent, world: SimWorld) => void;
  /** Her render karesinin sonunda (istatistik örnekleme). */
  onFrame?: (g: Game, dtReal: number) => void;
}
/** Maçtan çıkış. 'leave' YALNIZ ağ maçında: oyuncu odadan da ayrılıyor demek (oturum kapanır) — 'menu' ise
 *  ağda "lobiye dön" anlamına geliyor, çünkü oturum yaşamaya devam ediyor. */
export type GameExit = 'rematch' | 'menu' | 'leave';

/** Kare istatistikleri (window.__game.stats, ?stats=1, duman testi). Süreler bu karenin toplamı (ms): msSim yalnız `world.step()`
 *  (ARCHITECTURE §10 "Sim tick < 0.3 ms" bütçesi bunu ölçer), msBot bot think/frame, msPresent olay tüketimi (kamera/vfx/ses/hud — DOM'a
 *  dokunur, Audio createBufferSource), msView görünüm güncellemesi (render hariç; stage.stats.msRender).
 *  eventsDropped: olay havuzu (EVENT_CAP/tick) taşınca düşen olayların KÜMÜLATİF sayısı — taşma vfx/ses/HUD olaylarını sessizce
 *  kaybeder; burada görünür kılınır (?stats satırı, duman sonucu). */
export interface GameStats {
  msSim: number; msBot: number; msPresent: number; msView: number;
  ticks: number; frames: number; interpolate: boolean; snap60: boolean; rafMedianMs: number;
  eventsDropped: number;
}

export interface GameDebug {
  game: Game; world: World; bots: Bot[]; events: string[]; stats: GameStats; moveIds: string[]; tutorial: TutorialMode | null;
  /** `?record=1` ile açıldıysa o ana kadarki girdi kaydı (JSON); yoksa null. `tools/replay.mjs` bunu okur. */
  recording: () => RecordingJson | null;
  /** Yerel insan oyuncular (canlı liste): tarayıcı testi "kim hangi dövüşçü, hangi pad, bağlı mı" diye sorabilsin. */
  players: readonly LocalPlayerSlot[];
}

const _aim = new THREE.Vector3();
const _p: Vec3 = { x: 0, y: 0, z: 0 };
/** Sekme gizli mi (rAF'ın durduğu hâl). `document` yoksa (test) her zaman görünür sayılır. */
const isHidden = (): boolean => typeof document !== 'undefined' && document.visibilityState === 'hidden';

export class Game {
  readonly world: World;
  readonly bots: Bot[] = [];
  /** Eğitim kuklası (yalnız `tutorial`); normal maçta null. */
  readonly dummy: DummyBot | null = null;
  /** Eğitim denetleyicisi (ders paneli + kurallar); normal maçta null. */
  readonly tutorial: TutorialMode | null = null;
  /** Klavye+mouse (YALNIZ P1'e bağlanır; aynı klavyeyi iki kişi paylaşamaz — localPlayers.ts kural 2). */
  readonly keyboard = new KeyboardMouseSource();
  /** Yerel oyuncu kadrosu: oyuncu başına InputManager + GamepadSource('exact') + Rumble (input/localPlayers.ts). */
  readonly localPlayers = new LocalPlayers();
  readonly views: (FighterView | null)[] = [];
  readonly controllers: (AnimControllerImpl | null)[] = [];
  readonly arena: ArenaView;
  readonly items: ItemViews;
  readonly cam: ArenaCamera;
  readonly vfx: Vfx;
  readonly hud: Hud;
  readonly focusId: number;
  readonly stats: GameStats = {
    msSim: 0, msBot: 0, msPresent: 0, msView: 0, ticks: 0, frames: 0, interpolate: false, snap60: true, rafMedianMs: 0, eventsDropped: 0,
  };
  /** Son olaylar (window.__game.events; debug). */
  readonly eventLog: string[] = [];
  paused = false;
  running = false;
  private readonly moveIds: string[];
  private readonly weaponLenR = new Float64Array(8);
  private readonly weaponLenL = new Float64Array(8);
  /** Kare saati (frameLoop.ts): acc, snap60, interpolate, ölçüm. */
  private readonly clock: FrameClock = createFrameClock();
  private last = -1;
  private raf = 0;
  /** Oyuncu başına son okunan ham düğme durumu (Start kenarı; index = oyuncu numarası, dövüşçü değil). */
  private readonly prevRawButtons = new Int32Array(MAX_LOCAL_PLAYERS);
  /** Oyuncu başına son okunan kurtulma basışı sayısı (mash_tick kenarı; bkz. pollMashTick). */
  private readonly mashPresses = new Int32Array(MAX_LOCAL_PLAYERS);
  /** Sahipsiz pad taraması için bir sonraki zaman (ms): P1 pad'sizken saniyede ~4 kez, bkz. adoptPad. */
  private adoptAt = 0;
  private exitResolve: ((r: GameExit) => void) | null = null;
  private pauseEl: HTMLElement | null = null;
  /** Duraklatma kaplamasının pad gezintisi (açıkken kurulur, devam edilince bırakılır). */
  private pauseNav: ReturnType<typeof attachOverlayNav> | null = null;
  /** Kazanan panelinin pad gezintisi (maç bitince kurulur, çıkışta bırakılır). */
  private winnerNav: ReturnType<typeof attachOverlayNav> | null = null;
  /** Performans kutusu açık mı (`I` ile değişir; `?stats=1` başlangıç değerini verir). HUD'a aynası yazılır. */
  private statsOn = false;
  /** `I`: fps / sim-view-render süreleri / draw call kutusunu aç-kapat. Oyunun kendi tuşlarıyla çakışmıyor
   *  (J K L E Q C T P W A S D, ok tuşları, Space, Shift, Escape, F tam ekran, F3 hitbox, N B R eğitim). */
  private readonly onStatsKey = (e: KeyboardEvent): void => {
    if (e.code !== 'KeyI' || e.ctrlKey || e.altKey || e.metaKey || e.repeat) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    this.statsOn = !this.statsOn;
    this.hud.setStatsVisible(this.statsOn);
  };
  private readonly onPauseResume = (): void => this.setPaused(false);
  // Ağ maçında duraklatmadan çıkmak ODADAN ayrılmaktır (soket kapanır, koltuk boşalır, diğer oyuncuda bot
  // devralır); çevrimdışında eskisi gibi menüye döner.
  private readonly onPauseMenu = (): void => this.exit(this.opts.net ? 'leave' : 'menu');
  /** Duraklatma kaplamasındaki "Tam ekran": fare/klavye için. Pad'in A'sı sentetik tıklama ürettiğinden tarayıcı isteği
   *  reddedebilir (gamepad kullanıcı jesti sayılmaz) — bu durumda sessizce hiçbir şey olmaz (fullscreen.ts yutar). */
  private readonly onPauseFullscreen = (): void => { void toggleFullscreen(); };
  private readonly onResize = (): void => { this.vfx.setViewportHeight(this.opts.stage.canvas.clientHeight || 600); restartRateMeasure(this.clock); };
  /** Sekme gizlenince duraklat (DESIGN §13 madde 5; rAF zaten durur). Duman testinde (yalnız botlar, players boş) duraklatma yok:
   *  #pause kaplaması açılmasın, ölçüm wall-clock ile bitsin (bulgu 3). Görünür olunca ekran hızı yeniden ölçülür (önceki karar korunur). */
  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible') { restartRateMeasure(this.clock); return; }
    // Maç bittiyse (KAZANAN paneli açık) duraklatma kaplaması açılmaz: sonucun üstüne binip iki "Menü" düğmesi gösterirdi
    // (pollPauseButton ile aynı kural, 3. tur entegrasyon bulgusu).
    if (this.opts.players.length > 0 && this.world.match.phase !== 'over') this.setPaused(true);
  };

  constructor(readonly opts: GameOptions) {
    const { stage, assets } = opts;
    if (opts.fighters.length < 1 || opts.fighters.length > 8) throw new Error('fighters 1..8');
    if (opts.net && opts.tutorial) throw new Error('ağ maçında eğitim modu yok');
    // ── sim: AĞ MAÇINDA dünya istemciden gelir (sunucunun seed'i + kadrosu; istemci hiçbir şey uydurmaz —
    //    NETWORK.md değişmez 13). Çevrimdışında burada kurulur.
    this.world = opts.net?.world ?? createWorld({
      seed: opts.seed | 0,
      fighters: opts.fighters.map((f) => ({ charIdx: f.charIdx, isBot: f.isBot })),
      systems: realSystems(),
      clips: assets.clipData,
      countdown: opts.countdown ?? true,
      // Eğitimde eşya doğma noktaları boş: kılıç yalnız ders isteyince doğar (TUTORIAL §6).
      arena: opts.tutorial ? tutorialArena() : undefined,
    });
    if (opts.net === undefined) resolveMoveClips(this.world);   // ağ maçında istemci zaten çözdü
    this.world.snapshotPrev();
    // GİZLİ SEKME SAATİ (proposals/multiplayer.md §4.13). Sekme arka plana düşünce rAF durur ve `frame()`
    // çağrılmaz; ağ maçında bu ölümcül: sim yerinde sayarken sunucu akmaya devam eder ve istemci girdi halkasının
    // (256 tick) DIŞINA düşer — o noktadan sonra koşacağı tick'in girdisi artık yoktur ve FAZ 1'de tam durum
    // düzeltmesi olmadığı için bir daha ASLA yetişemez. ÖLÇÜLDÜ: gizli sekmede istemci 3000 tick geride kalıp
    // kilitlendi. Çözüm: WebSocket mesajları arka planda KISILMAZ → sim'i onların üstünden sür. Yalnız SİM koşar;
    // çizim, ses ve olay sunumu atlanır (görünmeyen sekmede zaten anlamsız), sekme geri gelince dünya günceldir.
    if (opts.net) {
      opts.net.setHiddenClock(() => {
        if (!isHidden() || this.disposed) return;
        const w = this.world;
        for (let i = 0; i < T.NET.catchUpMaxSteps; i++) {
          if (opts.net!.step(1) === 0) break;
          w.events.clear();                                  // sunum atlanıyor: olaylar birikmesin
        }
        this.world.snapshotPrev();                           // sekme geri gelince interpolasyon sıçramasın
      });
    }
    // Kayıt: kadro + seed kabın içinde saklanır ki oynatan taraf dünyayı aynen kurabilsin.
    // EĞİTİM KAYDEDİLEMEZ: TutorialMode dünyayı `world.step()` DIŞINDA değiştiriyor (canları geri veriyor, kuklayı
    // yerinde tutuyor, ders isteyince kılıç doğuruyor). Bunların hiçbiri girdi akışında olmadığı için kayıt
    // oynatıldığında bambaşka bir maç çıkar. Sessizce yanlış bir kayıt üretmektense açıkça reddediyoruz.
    if (opts.record && opts.tutorial) throw new Error('eğitim modu kaydedilemez: TutorialMode sim durumunu tick akışının dışında değiştiriyor');
    // Dünya kurulumunu değiştiren HER şey kayda giriyor (seed, kadro, geri sayım, arena) — yoksa oynatma sessizce
    // BAŞKA bir maç kurar.
    this.recorder = opts.record
      ? new InputRecorder(opts.seed | 0, opts.rosterSeed ?? 0,
          opts.fighters.map((f) => ({ charIdx: f.charIdx, isBot: f.isBot })),
          opts.countdown ?? true, 'default')
      : null;
    this.moveIds = this.world.moves.map((m) => m.id);
    // Eğitimde İLK bot Manken kuklasıdır (aynı `Bot` arayüzü; ders modunu TutorialMode değiştirir), kalanlar normal bot.
    for (let id = 0; id < opts.fighters.length; id++) {
      const f = opts.fighters[id]!;
      if (!f.isBot) continue;
      if (opts.net) continue;      // AĞ: botlar YALNIZ otoritede koşar; çıktıları yayından geliyor (NETWORK.md §3)
      if (opts.tutorial && this.dummy === null) {
        const d = createTutorialDummy(id, (opts.seed ^ (id * 0x9e3779b1)) | 0);
        this.dummy = d; this.bots.push(d);
        continue;
      }
      this.bots.push(createBot(id, f.difficulty));
    }
    // ── girdi: oyuncu başına InputManager + pad (input/localPlayers.ts). Klavye YALNIZ P1'de (aynı klavyeyi iki kişi
    //    paylaşırsa birbirinin tuşuna basar); P2–P4 kendi pad'iyle oynar. Oyuncu YOKSA (duman/izleyici) hiçbiri kurulmaz.
    if (opts.players.length > 0) {
      this.keyboard.attach(stage.canvas);
      this.localPlayers.attachKeyboard(this.keyboard, false);   // join = false: katılma aşağıdaki joinAt ile, numarası korunarak
    }
    // Kadrodaki insan dövüşçüler (roster.ts onları başa koyar): fighterId verilmemişse sırayla eşlenir.
    const humanIds: number[] = [];
    for (let i = 0; i < opts.fighters.length; i++) if (!opts.fighters[i]!.isBot) humanIds.push(i);
    let nextHuman = 0;
    for (const lp of opts.players) {
      // padSlot "kaçıncı bağlı pad"tir; ham yuva oynak olduğu için (bkz. gamepad.ts) burada BİR kez çözülür ve
      // oyuncunun kimliği o ham yuvaya sabitlenir ('exact' kip): pad koparsa girdi nötr olur, başkasının pad'ine düşmez.
      const raw = lp.padSlot >= 0 ? padSlotToIndex(lp.padSlot) : -1;
      // Pad çözülemezse (menüden sonra koptu / yuvası başkasına düştü) oyuncu YİNE kadroda kalır, yalnız girdisi nötr
      // olur: dövüşçüsü ortada sahipsiz kalmasın, HUD'da kendi renginde görünsün, kamera onu "yerel" saymayı sürdürsün.
      const slot = this.localPlayers.joinAt(lp.index, raw) ?? this.localPlayers.joinAt(lp.index, -1);
      if (!slot) continue;
      slot.charIdx = lp.charIdx;
      slot.fighterId = lp.fighterId >= 0 ? lp.fighterId : (humanIds[nextHuman++] ?? -1);
      slot.input.cameraYaw = 0;   // DESIGN §10 sabit yaw
    }
    // Nişan yardımı (mouse imleci → yaw) YALNIZ klavye+mouse oyuncusunda: imleç tek, P2–P4 pad'le nişan alır.
    const kb = this.localPlayers.playerAt(KEYBOARD_PLAYER);
    if (kb?.joined) kb.input.setAimResolver((nx, ny) => this.resolveAim(nx, ny));
    const first = this.localPlayers.players[0];
    this.focusId = first && first.fighterId >= 0 ? first.fighterId : 0;
    // Kamera / HUD "yerel oyuncu" kümesi: kimlikler burada belli (maç içinde değişmez) → bir kez kurulur.
    // Kadraj DEĞİŞMEZ (kamera zaten tüm dövüşçüleri çerçeveliyor); bu küme yalnız sarsıntı kademesini ve HUD renklerini sürer.
    const localIds = this.localPlayers.players.map((p) => p.fighterId);

    // ── görünümler
    this.arena = new ArenaView(ARENA, assets.kitGeometries, kitMaterial());
    stage.scene.add(this.arena.group);
    stage.fitArena(ARENA.halfWidth, ARENA.center);
    stage.resetQualityCeiling();
    this.cam = new ArenaCamera(stage.camera, ARENA, localIds, opts.seed | 0);
    this.items = new ItemViews(stage.scene, createProp);
    this.vfx = new Vfx({
      // Kenar halkası ("düşmek üzeresin") her YEREL oyuncu için güçlü kırmızı: dört kişilik ekranda yalnız P1'in
      // uyarılması, P2–P4'ü rakip gibi soluk halkayla bırakırdı (isLocal verilmezse eski tek-oyuncu yolu).
      scene: stage.scene, playerId: this.focusId, isLocal: (id) => this.localPlayers.isLocalFighter(id),
      posOf: (id, out) => { const v = this.views[id]; if (!v) return false; v.worldPos(out); return true; },
      jointWorld: (id, joint, out) => { const v = this.views[id]; if (!v) return false; v.jointWorld(out, joint); return true; },
      flash: (id, sec) => { this.views[id]?.flash(sec); },
    });
    this.vfx.setViewportHeight(stage.canvas.clientHeight || 600);
    this.hud = new Hud({
      // Canlı liste: her yerel oyuncunun etiketi/kartı KENDİ renginde (playerColor(index)); küme boşsa hiçbiri işaretlenmez.
      camera: stage.camera, localPlayers: this.localPlayers.players, chars: CHARACTERS,
      headWorld: (id, out) => { const v = this.views[id]; if (!v) return false; v.headWorld(out); return true; },
      container: opts.hudContainer, stats: opts.stats === true,
      winnerActions: opts.net ? 'net' : 'local',
      // Ping YALNIZ ağ maçında verilir: çevrimdışı maçta gecikme yok, "0 ms" yazmak yanlış bilgi olurdu.
      ...(opts.net ? { pingOf: (id: number): number => opts.net!.pingOf(id) } : {}),
      // Kazanan panelinin düğmeleri: ses BURADA (fare tıklaması da, pad'in b.click()'i de buradan geçer; pad yolu
      // ayrıca uiNav'dan 'select' yollar ama Audio aynı adı kare başına bir kez çalar → çift ses yok).
      onRematch: () => { this.opts.audio.ui('select'); this.exit('rematch'); },
      onMenu: () => { this.opts.audio.ui('select'); this.exit('menu'); },
    });
    for (let i = 0; i < 8; i++) { this.views.push(null); this.controllers.push(null); }
    // ── eğitim modu: ders paneli + kurallar (kukla varsa; yoksa sessizce normal maç)
    if (opts.tutorial && this.dummy) {
      this.tutorial = new TutorialMode({
        world: this.world, playerId: this.focusId, dummyId: this.dummy.fighterId, dummy: this.dummy,
        audio: opts.audio, container: opts.hudContainer, isBlocked: () => this.paused,
      });
      addEventListener('keydown', this.onTutorialKey);
    }
    addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);
    // Performans kutusu: ?stats=1 başlangıcı, sonra `I` ile açılıp kapanır (HUD'a da yaz ki ilk durum eşleşsin).
    this.statsOn = opts.stats === true;
    this.hud.setStatsVisible(this.statsOn);
    addEventListener('keydown', this.onStatsKey);
  }

  /** Eğitim kısayolları (TUTORIAL §1): N sonraki · B önceki · R baştan. Oyun tuşlarıyla çakışmaz (controls.ts'te yoklar). */
  private readonly onTutorialKey = (e: KeyboardEvent): void => {
    if (!this.tutorial || e.repeat || e.ctrlKey || e.altKey || e.metaKey) return;
    if (this.tutorial.handleKey(e.code)) e.preventDefault();
  };

  /** Dövüşçü modelleri (önbellekten klon) + görünümler; yükleme ekranından sonra bir kez. */
  async init(): Promise<void> {
    const { stage, assets } = this.opts;
    const models = await Promise.all(this.world.fighters.map((f) => loadCharacter(CHARACTERS[f.charIdx]!)));
    this.world.fighters.forEach((f, i) => {
      const def = CHARACTERS[f.charIdx]!;
      const view = new FighterView(f, def, models[i]!, {
        scene: stage.scene,
        createController: (root, ff) => { const c = createAnimController(root, assets.lib); this.controllers[ff.id] = c; return c; },
        decide,
        createProp,
        viewOf: (id) => this.views[id] ?? null,
        onFootstep: (ff, side) => this.opts.audio.footstep(ff, side),
        // AĞ: rakipler onaylanmış geçmişten çizilir (kendi dövüşçümüz için `netPose` false döner).
        netPose: this.opts.net ? (id, a, out) => this.opts.net!.netPose(id, a, out) : null,
      });
      this.views[f.id] = view;
    });
    // ilk kare: nötr karar yerine animMap kararı (T-pose görünmesin)
    for (const v of this.views) v?.update(this.world, 1, 0);
    this.exposeDebug();
  }

  /* ---------------------------------------------------------------- çalıştırma */

  /** Döngüyü başlatır; Tekrar/Menü ile biter. */
  run(): Promise<GameExit> {
    this.start();
    return new Promise<GameExit>((res) => { this.exitResolve = res; });
  }
  start(): void {
    if (this.running) return;
    this.running = true; this.last = -1;
    void loadLazySets(this.opts.assets.lib, loadGLTF);   // CR/SP/TL arka planda (ARCHITECTURE §5.1); gelene kadar yedekler
    this.raf = requestAnimationFrame(this.onFrame);
  }
  exit(reason: GameExit): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.setPaused(false);
    this.winnerNav?.stop(); this.winnerNav = null;   // panel kapanıyor → pad gezintisi de bırakılsın
    this.hud.hideWinner();
    const res = this.exitResolve; this.exitResolve = null;
    res?.(reason);
  }

  private readonly onFrame = (now: number): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.onFrame);
    this.frame(now);
  };

  /** Ağır çekim penceresi (DESIGN §10/§13): SUNUM durumu, sim'in değil (NETWORK.md karar 4) — hash'e girmez, her
   *  istemcide farklı olabilir. Kural ve eğri `view/slowMo.ts`te (saf, DOM'suz test edilir); burası yalnız
   *  `Ev.Downed` / `Ev.MatchEnd` olaylarını ona bağlar ve "benim dövüşçüm işin içinde mi"yi cevaplar. */
  private readonly slowMo: SlowMoState = createSlowMo();
  /** Koşulan tick'in BAŞINDAKİ maç fazı — olaylar step'ten sonra tüketildiği için `world.match.phase` artık
   *  adım 9'un sonucunu taşıyor; ağır çekim kapısı olayın gerçekleştiği andaki fazı sormalı. */
  private phaseAtTick: MatchState['phase'] = 'countdown';
  /** `?record=1` ile açılır; her tick kuantalanmış girdileri saklar (NETWORK.md §7.3). */
  private readonly recorder: InputRecorder | null;
  /** `dispose()` sonrası gizli sekme saati sim'i sürmeye devam etmesin. */
  private disposed = false;
  /**
   * Zaman çarpanı. Vuruş penceresinde SABİT çarpan değil bir EĞRİ döner (plato ×0.25, sonra 1.0'a çıkış rampası):
   * kullanıcı "ağır çekim daha keyifli olmalı, vuruşu detaylı izlemeliyim Party Animals gibi" dedi — sabit ×0.30
   * yavaşlatıyordu ama "an" üretmiyordu.
   * ÇEVRİMDIŞI: yalnız accumulator'a uygulanır → sim tick'i, hitstop ve kare verisi değişmez, determinizm korunur.
   * AĞ MAÇI: 1 döner — sim saati sunucuya kilitli, hiçbir istemci tick üretim hızını değiştiremez (değişmez 12).
   * Ağır çekimin ağdaki karşılığı render saatinin geride kalıp sonra yetişmesidir (NETWORK.md §2.1); o iş FAZ 2'de.
   * Kamera yakınlaşması ve ses perdesi ağda da çalışıyor — yalnız ZAMAN genişlemesi kapalı.
   */
  timeScale(): number { return this.opts.net ? 1 : timeScaleOf(this.slowMo, this.world.tick); }

  /** ARCHITECTURE §3 `frame(now)` birebir (saat: frameLoop.ts). Süre ölçümü ayrı ayrı (bulgu 1): msSim yalnız `world.step()`
   *  (ARCHITECTURE §10 "Sim tick < 0.3 ms" bütçesi), msBot bot think/frame, msPresent olay tüketimi (DOM/Audio), msView görünüm. */
  frame(now: number): void {
    const { stage } = this.opts;
    const clock = this.clock;
    const dtRaw = this.last < 0 ? TICK_DT : Math.min((now - this.last) / 1000, DT_CAP);
    this.last = now;
    if (sampleRate(clock, dtRaw * 1000)) { this.stats.rafMedianMs = clock.rafMedianMs; this.stats.snap60 = clock.snap60; this.stats.interpolate = clock.interpolate; }
    const dtReal = clampDt(clock, dtRaw);                                       // (1) dt snap
    this.updateKeyEdgeBrake();                                                  // poll'dan ÖNCE: kırpma reduce() içinde uygulanır
    this.localPlayers.poll(now);                                                // pad bağlantı durumu + titreşim yönlendirmesi
    this.adoptPad(now);                                                         // P1 pad'siz başladıysa: maç içinde takılan pad'i al
    this.localPlayers.pollInputs();                                             // her oyuncunun kenar bitleri biriktirilir
    this.pollPauseButton();
    const ts = this.timeScale();
    this.opts.audio.setTimeScale(ts);   // ağır çekimde ses de yavaşlar (perde düşer) — yavaşlamanın okunmasını sağlayan şey bu
    this.opts.audio.beginFrame();
    // TICK SÜRÜCÜSÜ. Çevrimdışı: accumulator (slow-mo yalnız acc'ye, tavana çarpınca artık atılır).
    // AĞ: sim saati YEREL — adım sayısı paket VARIŞINDAN değil, çevrimdışıyla aynı sabit adımlı biriktiriciden
    // geliyor. Eskiden `min(net.pending, …)` idi, yani dünya tam olarak paketler geldiğinde ilerliyordu; yoldaki
    // öbeklenme (ölçüm: T.NET yorumunda) doğrudan göze vuruyor, karelerin ~%20'sinde dünya donup sonra 6-7 tick
    // birden atlıyordu — 60 FPS'te bile "takılma" diye görülen şey buydu. Sunucuya kilit `netRate` ile korunuyor
    // (değişmez 12: istemci sunucuyu bekletemez, yalnız KENDİ hızını yüzde birkaç düzeltir).
    const net = this.opts.net;
    let steps: number;
    if (net) {
      this.consumePlayerInput();                                                // yerel girdiyi topla…
      const mine = this.world.inputs[net.yourFighterId] ?? this.world.inputs[0]!;
      // Kaplama açıkken girdi NÖTR'lenir: ağda sim durmadığı için aynı tuş hem kaplamayı gezdirip hem
      // dövüşçüyü sürerdi — pad'de A hem "Maçtan ayrıl"a basıp hem yumruk atardı.
      if (this.paused) { mine.moveX = 0; mine.moveZ = 0; mine.buttons = 0; mine.pressed = 0; mine.released = 0; }
      net.sendInput(mine);                                                      // …ve otoriteye gönder
      // Zaman genişletmesi UYGULANMAZ (ts değil, 1): ağır çekim ağda yalnız sunum katmanında yaşar.
      // `paused` YOK SAYILIR: sunucu beklemez (NETWORK.md değişmez 12). Yerelde durursak sunucunun gerisine
      // düşeriz ve girdi halkasının dışına taşınca bir daha yetişilemez — belirti "bağlantı koptu" oluyordu.
      // Kaplama açık kalır (ayrılma/tam ekran için), maç arkasında akmaya devam eder.
      steps = stepAccumulator(clock, dtReal * this.netRate(net), 1, false, true);
    } else {
      steps = stepAccumulator(clock, dtReal, ts, this.paused);
    }
    const world = this.world;
    // Rakip çiziminin geçmiş imleci GERÇEK ZAMANLA ilerler (paket varışıyla değil — bkz. client.advancePoseClock).
    if (net) net.advancePoseClock(dtReal);
    let msSim = 0, msBot = 0, msPresent = 0;
    for (let s = 0; s < steps; s++) {
      const tb = performance.now();
      this.phaseAtTick = world.match.phase;                                    // olay tüketimi bunu okur (bkz. consumeEvents)
      const t0 = performance.now();
      if (net) {
        // AĞ: girdi otoriteden geliyor, botlar burada koşmuyor, kuantalama zaten telde yapıldı. `step` tam olarak
        // ONAYLI tick'leri koşar — onaylanmamış tick'e asla girilmez (FAZ 1'de tahmin yok).
        if (net.step(1) === 0) { break; }
      } else {
        world.snapshotPrev();
        this.consumePlayerInput();                                             // (4) biriktirileni yaz, sıfırla (her insan için)
        for (let i = 0; i < this.bots.length; i++) {
          const b = this.bots[i]!;
          if (world.tick >= b.nextThinkTick) b.think(world);
          b.frame(inputOf(world, b.fighterId), world.tick);
        }
        // TEL FORMATINA İNDİRGE — sim'in gördüğü sayı ile telde gidecek sayı AYNI olsun (NETWORK.md §3.2).
        // İnsan ve bot ayrımı yok: otoritede botlar da girdi üretip yayınlayacak. Çevrimdışında da uygulanıyor ki
        // tek kod yolu kalsın ve kayıt/oynatma her yerde birebir olsun. His etkisi yok (çubuk 1/127, yaw 0.0055°).
        quantizeAndRecord(world.inputs, this.recorder);
        world.step();
      }
      const t1 = performance.now();
      // Eğitim: koruma kuralları olaylar TÜKETİLMEDEN önce (ürettiği Ev.Respawn / Ev.ItemSpawn aynı tick'te sunulsun),
      // ders ilerlemesi ve panel olaylardan SONRA (ilerleme → geçiş sırası doğru olsun).
      this.tutorial?.beforePresent();
      this.consumeEvents();
      this.pollMashTick();
      this.tutorial?.afterPresent();
      world.events.clear();
      const t2 = performance.now();
      msBot += t0 - tb; msSim += t1 - t0; msPresent += t2 - t1;
      this.stats.ticks++;
    }
    this.stats.msSim = msSim; this.stats.msBot = msBot; this.stats.msPresent = msPresent;
    const alpha = renderAlpha(clock, ts);                                       // (3) slow-mo'da interpolasyon zorla açık
    const dtView = this.paused && !net ? 0 : dtReal;   // ağda dünya akıyor: görüntü de akmalı

    const tv = performance.now();
    for (let i = 0; i < this.views.length; i++) {
      const v = this.views[i];
      if (!v) continue;
      v.update(world, alpha, dtView);
      this.syncWeaponLength(i);
    }
    this.items.update(world, alpha, dtView);
    this.cam.update(world, alpha, dtView, this.slowMo);
    this.vfx.update(dtView, world);
    this.hud.update(world, dtView);   // duraklatmada HUD zamanlayıcıları (RING OUT bandı, geri sayım, kenar flaşı) da donar — görünümle tutarlı
    this.opts.debugDraw?.update(world);
    this.tutorial?.pollShortcuts();   // pad R2/L2 (gerçek zaman; sim tick'i değil)
    this.stats.msView = performance.now() - tv;
    stage.render();
    this.stats.frames++;
    if (this.statsOn) {
      const ss = stage.stats;   // gölge geçişi ayrı raporlanır (orkestratör kararı 2: bütçe ≤ 130 sahne geçişine)
      this.hud.setStats({
        msSim, msBot, msPresent, msView: this.stats.msView, msRender: ss.msRender, fps: ss.fps, tier: ss.tier,
        drawCalls: ss.drawCalls, drawCallsShadow: ss.drawCallsShadow, drawCallsNoShadow: ss.drawCallsNoShadow,
        eventsDropped: this.stats.eventsDropped, timeScale: ts,
      });
    }
    this.opts.onFrame?.(this, dtReal);
  }

  /**
   * Klavye kenar freni bayrağı (inputManager.ts `keyEdgeBrake` sözleşmesi; DESIGN §3). WASD her zaman tam itme (|v| = 1.0 ≥
   * EDGE.brakeThreshold 0.60) ürettiğinden klavye oyuncusu kenar frenini hiç alamıyor, A basılı tutunca kendi kendine düşüyordu
   * (tur-2 düzeltmesi InputManager'a girmiş ama bayrağı kuran taraf hiç yazılmamıştı → 3. tur regresyon bulgusu).
   * Kural: oyuncu YERDE ∧ kenara < EDGE.alarmDist → bayrak açık; pad çubuğu boş değilse InputManager zaten kırpmaz.
   * poll()'dan ÖNCE çağrılır (kırpma `reduce()` içinde yapılır). Tahsis yok. HER İNSAN OYUNCU için AYRI hesaplanır:
   * bayrak o oyuncunun KENDİ dövüşçüsünün kenar mesafesinden gelir (tek bayrak paylaşılsaydı, P1 kenardayken P2'nin
   * klavyesi de kırpılırdı — pad oyuncusunda görünmez ama kurallar birbirine karışırdı). Oyuncu yoksa döngü boş.
   */
  private updateKeyEdgeBrake(): void {
    for (const p of this.localPlayers.players) {
      p.input.keyEdgeBrake = keyEdgeBrakeOn(p.fighterId >= 0 ? this.world.fighters[p.fighterId] : undefined);
    }
  }

  /** Biriktirilen girdi → sim girdisi (kural 4), oyuncu başına kendi dövüşçüsüne. Start biti sim'e geçmez: duraklatma kenarı
   *  pollPauseButton'da okunur, sim tamponu (stepInputBuffer pressTick[Start]) boşuna yazılmasın (bulgu 3). */
  private consumePlayerInput(): void {
    for (const p of this.localPlayers.players) {
      if (p.fighterId < 0) continue;
      const inp = inputOf(this.world, p.fighterId);
      p.input.consume(inp);
      inp.pressed &= ~Btn.Start; inp.released &= ~Btn.Start; inp.buttons &= ~Btn.Start;
    }
  }

  /**
   * P1 pad'siz başladıysa SAHİPSİZ ilk pad'i ona bağlar. Neden gerekli: tarayıcı bir pad'i ilk düğmeye basılana kadar
   * HİÇ göstermez — menüde pad'e dokunmayan oyuncunun kadrosunda `padSlot = −1` çıkar, maç içinde pad'e basınca
   * (bugünkü tek oyunculu davranış) çalışmaya devam etmeli. Yalnız P1 için: P2–P4'ün pad kimliği sabittir, aksi hâlde
   * biri diğerinin pad'ini kapardı. Kopan pad önce bırakılır (başka yuvada geri takılabilir). Saniyede ~4 kez.
   */
  private adoptPad(now: number): void {
    const kb = this.localPlayers.playerAt(KEYBOARD_PLAYER);
    if (!kb?.joined || now < this.adoptAt) return;
    this.adoptAt = now + 250;
    if (kb.padIndex >= 0) { if (kb.connected) return; this.localPlayers.unbindPad(KEYBOARD_PLAYER); }
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return;
    const pads = navigator.getGamepads();
    for (let i = 0; i < pads.length; i++) {
      if (!pads[i] || this.localPlayers.padOwner(i)) continue;
      // join()'in "evlat edinme" kuralı: klavye oyuncusu kadroda ve pad'siz olduğu için pad ONA bağlanır,
      // yeni oyuncu AÇILMAZ (maçta katılma yok — kadro menüde kapanır).
      this.localPlayers.join(i);
      return;
    }
  }

  /** Presenter: aynı karede biriken tick olayları sırayla (sarsıntı max, aynı ses ≤ 1). */
  private consumeEvents(): void {
    const ev = this.world.events, w = this.world;
    for (let i = 0; i < ev.count; i++) {
      const e = ev.items[i]!;
      this.cam.onEvent(e);
      this.vfx.onEvent(e, w);
      this.opts.audio.onEvent(e, w);
      this.hud.onEvent(e, w);
      this.rumbleEvent(e);
      // TICK: olayın KENDİ tick'i (`e.tick`), `w.tick` DEĞİL. Olaylar `world.step()`ten SONRA tüketiliyor ve
      // `step()` sonunda `tick++` oluyor → `w.tick` bir fazla. Eski kod pencereyi sim'in içinden, olayın kendi
      // tick'inde açıyordu; `w.tick` kullanmak pencereyi bir tick geciktirir ve bekleme süresini bir tick kaydırırdı.
      if (e.type === Ev.MatchEnd) { this.attachWinnerNav(); requestMatchEndSlowMo(this.slowMo, e.tick); }
      // DESIGN §10 mini ağır çekim: sim yalnız "bu isabet yere serdi/takla attırdı"yı bildirir (Ev.Downed), kararı
      // BURASI verir — yalnız BENİM dövüşçüm işin içindeyse (NETWORK.md karar 4; eski `!isBot` kuralı ağda yanlıştı).
      // FAZ: `w.match.phase` adım 9'dan SONRAKİ fazdır; son vuruş maçı bitirdiyse 'over' olur ve pencere hiç
      // açılmazdı — oysa eski kural adım 7'de, faz hâlâ 'fight' iken bakıyordu. `phaseAtTick` tick'in BAŞINDAKİ fazı
      // taşıyor (`step()`ten önce alınıyor), böylece bitirici vuruş da hak ettiği ağır çekimi alıyor.
      else if (e.type === Ev.Downed) {
        const mine = this.localPlayers.isLocalFighter(e.actor) || this.localPlayers.isLocalFighter(e.target);
        requestHitSlowMo(this.slowMo, e.tick, e.actor, e.target, this.phaseAtTick === 'fight', mine);
      }
      this.tutorial?.onEvent(e, w);
      this.logEvent(e);
      this.opts.onEvent?.(e, w);
    }
    // Havuz taşması sessiz kalmasın: EventPool.dropped EventList arayüzünde yok (needsWP0) → createEventList'in döndürdüğü havuza
    // daraltılır; başka bir EventList uygulamasında alan undefined → atlanır. Sayaç tick başına okunup sıfırlanır (kümülatif toplam stats'ta).
    const pool = ev as EventPool;
    if (pool.dropped) { this.stats.eventsDropped += pool.dropped; pool.dropped = 0; }
  }
  /**
   * Kurtulma mash'i (DESIGN §11 mash_tick): sim'de basış OLAYI yok — grab.ts yalnız sayaç tutuyor (tutuş/kaldırma/fırlatma
   * windup'ında `windupPresses`, taşımada `escapePresses`). Presenter bu sayaçların ARTIŞINDAN türetiyor; her tick okunur,
   * ses kare başına bir kez çalar (Audio.beginFrame). Yalnız insan oyuncu: 8 kişilik kaosta botların mash'i gürültü olurdu.
   * Taban tutulma bitince sıfırlanır — yeni tutuşta sayaçlar da sıfırdan başlıyor (grab.ts), yani ilk basış yine duyulur.
   * Çok oyuncuda her insan AYRI sayılır (P2 tutulduğunda da sesi duysun); Audio aynı adı kare başına bir kez çaldığı
   * için ikisi birden basarsa çift ses olmaz.
   */
  private pollMashTick(): void {
    for (const p of this.localPlayers.players) {
      const f = p.fighterId >= 0 ? this.world.fighters[p.fighterId] : undefined;
      const presses = f && f.grabbedBy !== NO_ID ? f.escapePresses + windupPressesOf(f) : 0;
      if (presses > (this.mashPresses[p.index] ?? 0)) this.opts.audio.mashTick();
      this.mashPresses[p.index] = presses;
    }
  }

  /**
   * Titreşim (pad haptics): DESIGN §11 "his cihazları" tablosunun dokunsal karşılığı — ekran sarsıntısıyla aynı
   * kademelendirme (hafif / ağır / knockdown / ring-out), ama YALNIZ olayın tarafı olan oyuncunun KENDİ pad'ine.
   * (Kamera sarsıntısındaki kuralla aynı gerekçe: 8 kişilik kaosta her isabette titreyen pad, oyuncunun kendi
   * vuruşunu kaybettirir.) Çok oyuncuda ekran ortaktır ama PAD ORTAK DEĞİLDİR: vuran P1'in pad'i "vurdum", yiyen
   * P2'nin pad'i "yedim" der. Vurulmak vurmaktan daha yumuşak: `hurt` zayıf motorda kalır.
   */
  private rumbleEvent(e: SimEvent): void {
    const lp = this.localPlayers;
    if (lp.count === 0) return;
    const actor = lp.playerOfFighter(e.actor), target = lp.playerOfFighter(e.target);
    if (!actor && !target) return;
    switch (e.type) {
      case Ev.Hit: {
        const kind = e.v2 as HitKind;
        if (kind === HitKind.Blocked || kind === HitKind.Parried) return;   // kendi olayları var
        const heavy = kind === HitKind.Finisher || kind === HitKind.Tumble;
        actor?.rumble.play(heavy ? RUMBLE.hitHeavy : RUMBLE.hitLight);
        if (target && target !== actor) target.rumble.play(RUMBLE.hurt);
        return;
      }
      case Ev.Blocked: this.rumbleBoth(actor, target, RUMBLE.block); return;
      case Ev.Parry: this.rumbleBoth(actor, target, RUMBLE.parry); return;
      case Ev.Knockdown: case Ev.GuardBreak: this.rumbleBoth(actor, target, RUMBLE.knockdown); return;
      case Ev.Grab: case Ev.PickUpFighter: case Ev.Hold: this.rumbleBoth(actor, target, RUMBLE.grab); return;
      case Ev.Throw: case Ev.BodyImpact: this.rumbleBoth(actor, target, RUMBLE.hitHeavy); return;
      case Ev.Fell: this.rumbleBoth(actor, target, RUMBLE.ringOut); return;
      default: return;
    }
  }
  /** Olayın iki tarafı da yerel oyuncuysa ikisinin pad'i de titrer (aynı yuvaya iki kez gitmesin). */
  private rumbleBoth(a: LocalPlayerSlot | null, b: LocalPlayerSlot | null, preset: RumblePreset): void {
    a?.rumble.play(preset);
    if (b && b !== a) b.rumble.play(preset);
  }

  /**
   * Kazanan paneli (hud `.rh-panel`) pad ile gezilebilsin: Tekrar / Menü düğmeleri D-pad + A ile seçilir.
   * HUD'un kendi `onEvent`i bu olayda paneli kurup gösterdiği için (presenter sırası: hud → burası) düğmeler hazırdır.
   * DOM'u hud.ts'ten sorgulamak yerine buradan bağlamak bilinçli: HUD katmanı girdi bilmiyor (game → view tek yön).
   * `keyboard: false` — Space da "aktifleştir" sayılsaydı, maç biterken zıplama tuşuna basılı oyuncu istemeden
   * "Tekrar"a basmış olurdu.
   */
  private attachWinnerNav(): void {
    if (this.winnerNav) return;
    const panel = document.querySelector('.rh-panel');
    if (panel instanceof HTMLElement) {
      // Burada 'select' geçebilir (duraklatmadan farklı olarak): Tekrar/Menü düğmelerinin sesini başka kimse çalmıyor.
      this.winnerNav = attachOverlayNav(panel, { keyboard: false, onNavSound: (kind) => this.opts.audio.ui(kind) });
    }
  }

  private logEvent(e: SimEvent): void {
    const mv = e.move >= 0 ? ' ' + (this.moveIds[e.move] ?? e.move) : '';
    this.eventLog.push(`${e.tick} ${evName(e.type)} a${e.actor} t${e.target}${mv} v${e.v0}`);
    if (this.eventLog.length > EVENT_LOG_CAP) this.eventLog.splice(0, this.eventLog.length - EVENT_LOG_CAP);
  }

  /** Silah ucu uzunluğu → AnimController (VFX izleri); yalnız değişince (WP7 setWeaponLength arayüz dışı ek). */
  private syncWeaponLength(id: number): void {
    const c = this.controllers[id]; const f = this.world.fighters[id];
    if (!c || !f) return;
    const items = this.world.items;
    const r = f.weaponItem >= 0 ? (items[f.weaponItem]?.length ?? 0) : 0;
    const lid = f.weaponItemL >= 0 ? f.weaponItemL : f.shieldItem;
    const l = lid >= 0 ? (items[lid]?.length ?? 0) : 0;
    if (this.weaponLenR[id] !== r || this.weaponLenL[id] !== l) { this.weaponLenR[id] = r; this.weaponLenL[id] = l; c.setWeaponLength(r, l); }
  }

  /* ---------------------------------------------------------------- duraklatma (DESIGN §13 madde 5) */

  /** Start basış kenarı (klavye Esc/P olay kenarı ya da pad prev/cur farkı) — sim'e girmeden burada okunur.
   *  HERHANGİ bir oyuncunun Start'ı duraklatır (kanepede kimse "bir dakika" diyemeyecek durumda kalmasın); kenar
   *  oyuncu başına ayrı tutulur, döngü erken kesilmez ki her oyuncunun prev durumu tazelensin. */
  private pollPauseButton(): void {
    let edge = 0;
    for (const p of this.localPlayers.players) {
      const raw = p.input.raw;
      edge |= ((raw.buttons & ~(this.prevRawButtons[p.index] ?? 0)) | raw.pressedSince) & Btn.Start;
      this.prevRawButtons[p.index] = raw.buttons;
    }
    if (!edge) return;
    // Maç bitti (KAZANAN paneli açık, Tekrar/Menü düğmeleriyle) → Escape DURAKLATILDI kaplamasını sonucun ÜSTÜNE açıyordu:
    // iki "Menü" düğmesi üst üste geliyor, sim zaten durmuş durumda "duraklatmanın" anlamı yok (3. tur entegrasyon bulgusu).
    // Kenar yine tüketilir (prevRawButtons yazıldı). Panel açıkken duraklatmadaysak (maç sonu duraklamada yakalanırsa) çıkışa izin var.
    if (this.world.match.phase === 'over') { if (this.paused) this.setPaused(false); return; }
    this.setPaused(!this.paused);
  }
  setPaused(on: boolean): void {
    if (this.paused === on) return;
    this.paused = on;
    if (!this.pauseEl) {
      this.pauseEl = document.getElementById('pause');
      if (this.pauseEl) {
        document.getElementById('pause-resume')?.addEventListener('click', this.onPauseResume);
        const pm = document.getElementById('pause-menu');
        // Ağda çıkış "menüye dön" değil "odadan ayrıl", başlık da yalan söylemesin: maç DURMUYOR.
        // Metinler HER kurulumda yazılıyor (koşulun iki dalı da): #pause paylaşılan bir DOM, bir ağ
        // maçından sonra çevrimdışı maça girildiğinde "MAÇ SÜRÜYOR" orada kalıyordu.
        const h = this.pauseEl.querySelector('h2');
        if (pm) pm.textContent = this.opts.net ? 'Maçtan ayrıl' : 'Menü';
        if (h) h.textContent = this.opts.net ? 'MAÇ SÜRÜYOR' : 'DURAKLATILDI';
        pm?.addEventListener('click', this.onPauseMenu);
        document.getElementById('pause-fs')?.addEventListener('click', this.onPauseFullscreen);
      }
    }
    if (this.pauseEl) {
      // Metinler burada da tazeleniyor: kaplama süreçte bir kez kuruluyor, ama sahibi maçtan maça değişiyor.
      const pm = document.getElementById('pause-menu');
      const h = this.pauseEl.querySelector('h2');
      if (pm) pm.textContent = this.opts.net ? 'Maçtan ayrıl' : 'Menü';
      if (h) h.textContent = this.opts.net ? 'MAÇ SÜRÜYOR' : 'DURAKLATILDI';
      this.pauseEl.hidden = !on;
    }
    // Kaplama sesi burada, kaplamanın kendisiyle birlikte: duraklatma Escape/Start, pad B ve "Devam" düğmesinden de
    // kapanabiliyor — tek yer olmasa her yola ayrı ses yazmak gerekirdi.
    this.opts.audio.ui(on ? 'select' : 'back');
    // Pad ile duraklatma kaplaması: Devam/Menü düğmeleri D-pad/çubukla gezilir, A onaylar, B devam eder.
    // Kaplamayı P1 SÜRER (UiNav ilk bağlı pad'i okur): tek imleçli bir menüyü dört kişi birden gezerse odak zıplar.
    // `keyboardBack: false` bilinçli — Escape hem UiNav.onBack'i hem pollPauseButton'ı tetikleyip duraklatmayı iki kez
    // çevirirdi (devam et → hemen tekrar duraklat). Oklar/Enter açık kalır, Escape'i yalnız oyunun kendi kancası görür.
    if (on && this.pauseEl && !this.pauseNav) {
      // Ses kancasından YALNIZ 'move' geçirilir: onay/geri sesini yukarıdaki setPaused satırı sahipleniyor ("Devam"a
      // basınca hem ui_select hem ui_back çalardı — kaplamadan çıkışın tek sesi ui_back olsun).
      this.pauseNav = attachOverlayNav(this.pauseEl, {
        onBack: () => this.setPaused(false), keyboardBack: false,
        onNavSound: (kind) => { if (kind === 'move') this.opts.audio.ui('move'); },
      });
    } else if (!on && this.pauseNav) {
      this.pauseNav.stop(); this.pauseNav = null;
    }
    // Duraklatınca hiçbir pad titremeye devam etmesin; devam ederken bekleyen kenarlar düşer (WP6: menüden/duraklatmadan dönüşte).
    if (on) for (const p of this.localPlayers.players) p.rumble.stop();
    else this.localPlayers.reset();
  }

  /* ---------------------------------------------------------------- nişan (mouse) */

  /** İmleç NDC → kamera ışını → oyuncu ayak düzlemi (y = pos.y) kesişimi → yaw (WP6 integrasyon notu).
   *  Yalnız klavye+mouse oyuncusu (P1) için kurulur; düzlem ONUN dövüşçüsünün ayak yüksekliğinde. */
  private resolveAim(nx: number, ny: number): number {
    const kb = this.localPlayers.playerAt(KEYBOARD_PLAYER);
    const f = kb && kb.fighterId >= 0 ? this.world.fighters[kb.fighterId] : undefined;
    if (!f) return NaN;
    const cam = this.opts.stage.camera;
    _aim.set(nx, ny, 0.5).unproject(cam).sub(cam.position).normalize();
    if (Math.abs(_aim.y) < 1e-5) return NaN;
    const t = (f.pos.y - cam.position.y) / _aim.y;
    if (t <= 0) return NaN;
    _p.x = cam.position.x + _aim.x * t; _p.z = cam.position.z + _aim.z * t;
    const dx = _p.x - f.pos.x, dz = _p.z - f.pos.z;
    return dx * dx + dz * dz < 0.04 ? NaN : yawOf(dx, dz);   // imleç karakterin üstündeyse yön belirsiz → bakış + yardım
  }

  /** Yerel sim saatini sunucu akışına kilitleyen hız çarpanı (denetleyici `frameLoop.netPlayoutRate`). */
  private readonly netClock = createNetClock();
  private netRate(net: NetClient): number {
    return netPlayoutRate(this.netClock, net.serverTick, this.world.tick, net.inputLeadTicks);
  }


  /* ---------------------------------------------------------------- debug */

  private exposeDebug(): void {
    const dbg: GameDebug = {
      game: this, world: this.world, bots: this.bots, events: this.eventLog, stats: this.stats, moveIds: this.moveIds,
      tutorial: this.tutorial, players: this.localPlayers.players,
      recording: () => (this.recorder ? toJson(this.recorder.finish()) : null),
    };
    (globalThis as unknown as { __game?: GameDebug }).__game = dbg;
  }

  /* ---------------------------------------------------------------- temizlik */

  dispose(): void {
    this.disposed = true;
    this.exit('menu');
    removeEventListener('keydown', this.onTutorialKey);
    this.tutorial?.dispose();
    removeEventListener('resize', this.onResize);
    removeEventListener('keydown', this.onStatsKey);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.getElementById('pause-resume')?.removeEventListener('click', this.onPauseResume);
    document.getElementById('pause-menu')?.removeEventListener('click', this.onPauseMenu);
    document.getElementById('pause-fs')?.removeEventListener('click', this.onPauseFullscreen);
    this.pauseNav?.stop(); this.pauseNav = null;      // pad kaplama gezintisi (kendi rAF'ı) sızmasın
    this.winnerNav?.stop(); this.winnerNav = null;
    for (const p of this.localPlayers.players) { p.rumble.stop(); p.input.setAimResolver(null); }
    this.localPlayers.detachKeyboard();
    this.keyboard.detach();
    for (let i = 0; i < this.views.length; i++) { this.views[i]?.dispose(); this.views[i] = null; this.controllers[i] = null; }
    this.items.dispose();
    this.vfx.dispose();
    this.arena.dispose();
    this.hud.dispose();
    const g = globalThis as unknown as { __game?: GameDebug };
    if (g.__game?.game === this) delete g.__game;
  }
}

/** Dövüşçü kadrosu için CharacterDef listesi (yükleme). */
export const charDefsOf = (fighters: readonly GameFighterSpec[]): CharacterDef[] => fighters.map((f) => CHARACTERS[f.charIdx]!);
/** Yardımcı: id aralık dışı → NO_ID. */
export const validId = (id: number, n: number): number => (id >= 0 && id < n ? id : NO_ID);
